import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiForm } from "./lib/api";
import { confirmDialog } from "./Confirm";
import { FileButton } from "./components/ui";

// ── WHO ALREADY VOTED (0355) ────────────────────────────────────────────────────────────────────
//
// Caleb, 2026-10-18, the day before early voting: the county puts out, every day, who voted the day
// before -- as a PDF -- and those people have to come out of the walkbooks so the field team spends
// its doors on people who have not voted yet.
//
// One upload here counts for EVERY campaign whose election is that day (0413) -- the campaigns on
// the Election calendar's row for it. A voter on a list:
//   * is never put in a new walkbook;
//   * leaves the walkbooks already out, and a home leaves its book once every targeted voter in it
//     has voted -- at the phone's next sync, with no recutting;
//   * shows "Voted" on the phone, on the person.
// It all stops the day after election day, on its own.
//
// THE FILE IS READ, THEN CHECKED, THEN SAVED -- and nothing is kept until Save. Reading refuses to
// guess: a line that does not hold one clean VUID is shown as a problem, and a file with a problem
// cannot be saved (backend/ballots/read_list.py).

type Read = {
  file_name: string; lines: number; rows: [string, string | null][]; vuids: number;
  first_cast: string | null; last_cast: string | null;
  problems: { line: number; text: string; why: string }[]; problem_count: number;
  other_count: number; other_sample: string[]; method_guess: string | null;
  how?: "picture"; ai?: { pages: number; cost_cents: number } | null;
};
type Item = { id: number; file: File; state: "reading" | "read" | "error" | "saving" | "saved"; read?: Read; err?: string; saved?: any; leaveOut?: boolean };
type Check = { vuids: number; in_orator: number; already: number;
               by_campaign: { campaign_id: string; campaign: string; doors: number; voters: number }[];
               campaigns_on_day: { campaign_id: string; campaign: string }[];
               // 0419: the call and text lists of those campaigns.
               phones_by_campaign?: { campaign_id: string; campaign: string; calls: PhoneEffect; texts: PhoneEffect }[] };
type PhoneEffect = { numbers: number; voters: number; open: number };
/** "call lists: 3 numbers leave (5 voters)". */
const phoneLine = (kind: "call" | "text", e: PhoneEffect) =>
  `${kind} lists: ${e.numbers.toLocaleString()} number${e.numbers === 1 ? "" : "s"} leave${e.numbers === 1 ? "s" : ""} (${e.voters.toLocaleString()} voter${e.voters === 1 ? "" : "s"})`;
// An election day on the calendar (0413): a list can only be filed under one of these.
type Day = { id: string; day: string; label: string; passed: boolean; campaigns: string[] };
type Upload = { id: string; election_date: string; method: string; file_name: string | null; vuid_count: number;
                in_orator: number; first_cast: string | null; last_cast: string | null; uploaded_at: string;
                uploaded_by: string | null; removed_at: string | null; counting: boolean;
                campaigns_on_day: string[]; campaign: string | null };

const METHODS: [string, string][] = [["mail", "Mail ballot received"], ["early", "Voted early in person"], ["eday", "Voted on election day"]];
const methodLabel = (m: string) => (METHODS.find(([k]) => k === m)?.[1] ?? m);

/** A calendar day, as the county wrote it. Never through Date(): "2026-10-07" is midnight UTC,
 *  which is the 6th in Texas. */
function day(iso: string | null): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : "—";
}
function span(a: string | null, b: string | null): string {
  if (!a) return "—";
  return a === b || !b ? day(a) : `${day(a)} – ${day(b)}`;
}

/** A request that never got an answer says "Failed to fetch", which tells nobody anything. */
function plain(e: any): string {
  const m = String(e?.message ?? e).replace(/^API \d+: /, "");
  if (/failed to fetch|networkerror|load failed/i.test(m)) return "The server didn't answer. Try again in a minute.";
  try { const j = JSON.parse(m); if (j?.detail) return String(j.detail); } catch { /* not JSON */ }
  return m;
}

let nextId = 1;

export function Voted() {
  const [items, setItems] = useState<Item[]>([]);
  const [election, setElection] = useState<string>("");
  const [days, setDays] = useState<Day[] | null>(null);
  const [daysErr, setDaysErr] = useState<string | null>(null);
  const [method, setMethod] = useState<string>("");
  const [check, setCheck] = useState<Check | null>(null);
  const [checkErr, setCheckErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [list, setList] = useState<{ uploads: Upload[]; counting: number; counting_in_orator: number } | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);
  const itemsRef = useRef<Item[]>([]);
  const put = (next: Item[]) => { itemsRef.current = next; setItems(next); };

  const load = () => {
    setListErr(null); setDaysErr(null);
    // THE DAYS TO CHOOSE FROM are the calendar's, not a typed date: a list filed under a day no
    // campaign is in would take nobody out, silently.
    const cal = api<{ elections: Day[] }>("/elections")
      .then((r) => { const up = r.elections.filter((e) => !e.passed); setDays(up); return up; })
      .catch((e) => { setDaysErr(plain(e)); return [] as Day[]; });
    api<{ uploads: Upload[]; counting: number; counting_in_orator: number }>("/ballots")
      .then(async (r) => {
        setList(r);
        const up = await cal;
        // THE ELECTION CARRIES OVER from the last list still counting -- the second day's upload is
        // for the same election as the first. Else the soonest day some campaign is in.
        const live = r.uploads.find((u) => u.counting);
        setElection((cur) => cur || live?.election_date || up.find((d) => d.campaigns.length)?.day || up[0]?.day || "");
      })
      .catch((e) => setListErr(plain(e)));
  };
  useEffect(load, []);

  /** A file whose bad lines the person has chosen to leave out counts; one with unanswered
   *  problems, or that failed, does not. */
  const usable = (x: Item) => x.state !== "error" && !!x.read && x.read.vuids > 0 && (x.read.problem_count === 0 || !!x.leaveOut);

  async function recheck(all: Item[], on: string = election) {
    setCheck(null); setCheckErr(null);
    const vu = Array.from(new Set(all.filter(usable).flatMap((x) => x.read!.rows.map((r) => r[0]))));
    if (!vu.length || !on) return;
    try { setCheck(await api<Check>("/ballots/check", { method: "POST", body: JSON.stringify({ vuids: vu, election_date: on }) })); }
    catch (e: any) { setCheckErr(plain(e)); }
  }
  // A DIFFERENT DAY IS A DIFFERENT SET OF CAMPAIGNS, so the check is asked again.
  function chooseDay(d: string) { setElection(d); recheck(itemsRef.current, d); }
  const dayLabel = (d: Day) =>
    `${day(d.day)} · ${d.label} · ${d.campaigns.length ? `${d.campaigns.length} campaign${d.campaigns.length === 1 ? "" : "s"}` : "no campaigns"}`;

  async function pick(files: ArrayLike<File> | null) {
    if (!files || !files.length) return;
    // PICKING ADDS TO THE SELECTION. Each file has its own ✕, so a wrong one comes off on its own
    // instead of by choosing everything again.
    const fresh: Item[] = Array.from(files).map((f) => ({ id: nextId++, file: f, state: "reading" }));
    let out = [...itemsRef.current.filter((x) => x.state !== "saved"), ...fresh];
    put(out); setDone(null);
    // ONE AT A TIME. A day's file takes a few seconds to read on the server; fifteen at once would
    // be fifteen of those competing for the same worker.
    for (const f of fresh) {
      const fd = new FormData(); fd.append("file", f.file);
      let upd: Partial<Item>;
      try { upd = { state: "read", read: await apiForm<Read>("/ballots/read", fd) }; }
      catch (e: any) { upd = { state: "error", err: plain(e) }; }
      out = itemsRef.current.map((x) => (x.id === f.id ? { ...x, ...upd } : x));
      put(out);
    }
    const guesses = new Set(out.map((x) => x.read?.method_guess).filter(Boolean) as string[]);
    if (!method) setMethod(guesses.size === 1 ? [...guesses][0] : "");
    recheck(out);
  }

  function remove(id: number) {
    const out = itemsRef.current.filter((x) => x.id !== id);
    put(out); recheck(out);
  }
  function leaveOut(id: number, on: boolean) {
    const out = itemsRef.current.map((x) => (x.id === id ? { ...x, leaveOut: on } : x));
    put(out); recheck(out);
  }

  const reading = items.some((x) => x.state === "reading");
  const readingNow = items.find((x) => x.state === "reading");
  const pending = items.filter((x) => x.state !== "saved");
  const blocked = pending.some((x) => !usable(x));
  const totalVoters = useMemo(() => new Set(pending.filter(usable).flatMap((x) => x.read!.rows.map((r) => r[0]))).size, [items]);
  const canSave = pending.length > 0 && !reading && !blocked && !!method && !!election && !saving;

  async function save() {
    if (!canSave) return;
    setSaving(true); setDone(null);
    let doors = 0, calls = 0, texts = 0, saved = 0, failed = 0;
    for (const it of pending) {
      const r = it.read!;
      put(itemsRef.current.map((x) => (x.id === it.id ? { ...x, state: "saving" } : x)));
      let upd: Partial<Item>;
      try {
        const s = await api<any>("/ballots/save", { method: "POST", body: JSON.stringify({
          file_name: r.file_name, election_date: election, method, lines: r.lines, rows: r.rows }) });
        upd = { state: "saved", saved: s }; doors += Number(s.doors_out || 0);
        calls += Number(s.calls_out || 0); texts += Number(s.texts_out || 0); saved++;
      } catch (e: any) { upd = { state: "error", err: plain(e) }; failed++; }
      put(itemsRef.current.map((x) => (x.id === it.id ? { ...x, ...upd } : x)));
    }
    setSaving(false); setCheck(null);
    setDone(failed
      ? `${saved} saved, ${failed} not saved — see the red lines above.`
      // WALKBOOKS, CALL LISTS AND TEXT LISTS (0419): the list does all three, so it says all three.
      : `Saved. ${doors.toLocaleString()} door${doors === 1 ? "" : "s"} left the walkbooks that are out now, ${calls.toLocaleString()} number${calls === 1 ? "" : "s"} left call lists and ${texts.toLocaleString()} left text lists; phones drop them at their next sync.`);
    load();
  }

  async function takeBack(u: Upload) {
    const ok = await confirmDialog(
      `Take back "${u.file_name || "this list"}"?\n\nIts ${u.vuid_count.toLocaleString()} voters stop counting as voted, and anyone it took off a walkbook, call list or text list goes back on — unless another list still has that voter.`,
      { danger: true, confirmLabel: "Take it back" });
    if (!ok) return;
    try { await api(`/ballots/${u.id}/remove`, { method: "POST" }); load(); }
    catch (e: any) { setListErr(plain(e)); }
  }

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 6, flexWrap: "wrap", alignItems: "baseline", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Who already voted</h2>
        <span className="muted" style={{ fontSize: 13 }}>The county's daily lists. Everyone on them leaves the walkbooks, call lists and text lists of the campaigns in that election, until the day after Election Day.</span>
      </div>

      <div className="card" style={{ padding: 16, marginTop: 12 }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Add the county's list</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ fontSize: 13 }}>
            <div className="muted" style={{ marginBottom: 4 }}>Files — pick several at once</div>
            {/* ONE LINE THAT CHANGES AS IT GOES (2026-11-01): the shared FileButton, as on the dashboard. */}
            <FileButton multiple accept=".pdf,.csv,.xlsx,.xlsm,.txt" onPick={pick} disabled={saving}
              label="＋ Choose files" again="＋ Add more files" busy={reading}
              file={readingNow ? readingNow.file.name : pending.length ? `${pending.length} file${pending.length === 1 ? "" : "s"}` : null}
              note={readingNow ? "reading the file, then checking it" : pending.length ? "listed below" : null}
              formats="PDF, Excel or CSV" />
          </div>
          <label style={{ fontSize: 13 }}>
            <div className="muted" style={{ marginBottom: 4 }}>Election day — from the Election calendar</div>
            <select className="ctl" value={election} onChange={(e) => chooseDay(e.target.value)} style={{ width: 360 }} disabled={!days}>
              {!days && <option value="">{daysErr ? "Couldn't load the calendar" : "Loading…"}</option>}
              {days && days.length === 0 && <option value="">No elections ahead on the calendar</option>}
              {days && election && !days.some((d) => d.day === election) && <option value={election}>{day(election)}</option>}
              {(days ?? []).map((d) => <option key={d.id} value={d.day}>{dayLabel(d)}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>
            <div className="muted" style={{ marginBottom: 4 }}>How they voted</div>
            <select className="ctl" value={method} onChange={(e) => setMethod(e.target.value)} style={{ width: 230 }}>
              <option value="">Choose…</option>
              {METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          {pending.length > 1 && !saving && (
            <button type="button" className="btn sm" style={{ marginBottom: 6 }} onClick={() => { put([]); setCheck(null); setCheckErr(null); }}>Clear all</button>
          )}
        </div>

        {items.length > 0 && (
          <table style={{ width: "100%", marginTop: 14, fontSize: 13 }}>
            <thead><tr className="muted" style={{ textAlign: "left" }}>
              <th>File</th><th>Voters read</th><th>Ballot dates</th><th>Lines skipped</th><th></th><th></th>
            </tr></thead>
            <tbody>
              {items.map((x) => (
                <tr key={x.id} style={{ verticalAlign: "top" }}>
                  <td>{x.file.name}
                    {/* A SCAN (0418): read by the AI picture reader, every number checked by name. */}
                    {x.read?.how === "picture" && <div className="muted" style={{ fontSize: 12 }}>
                      scanned picture · read by the AI{x.read.ai ? ` (${x.read.ai.pages} pages, ${x.read.ai.cost_cents.toFixed(1)}¢)` : ""} · every number checked against its name
                    </div>}
                  </td>
                  <td>{x.read ? x.read.vuids.toLocaleString() : "—"}</td>
                  <td>{x.read ? span(x.read.first_cast, x.read.last_cast) : "—"}</td>
                  <td className="muted">
                    {x.read && (x.read.other_count === 0 ? "none" : (
                      <details>
                        <summary style={{ cursor: "pointer" }}>{x.read.other_count.toLocaleString()} (headers, footers, lines with no VUID)</summary>
                        {x.read.other_sample.map((l, i) => <div key={i} style={{ fontSize: 12 }}>{l}</div>)}
                        {x.read.other_count > x.read.other_sample.length && <div style={{ fontSize: 12 }}>…and {x.read.other_count - x.read.other_sample.length} more</div>}
                      </details>
                    ))}
                  </td>
                  <td style={{ minWidth: 240 }}>
                    {x.state === "reading" && <span className="muted">Reading…</span>}
                    {x.state === "saving" && <span className="muted">Saving…</span>}
                    {x.state === "saved" && <span style={{ color: "var(--good)" }}>✓ Saved · {Number(x.saved?.doors_out || 0).toLocaleString()} doors out</span>}
                    {x.state === "read" && x.read && x.read.problem_count === 0 && x.read.vuids > 0 && <span style={{ color: "var(--good)" }}>✓ Every line read</span>}
                    {x.state === "read" && x.read && x.read.vuids === 0 && x.read.problem_count === 0 && <span style={{ color: "var(--bad)" }}>No VUIDs in this file</span>}
                    {x.state === "read" && x.read && x.read.problem_count > 0 && (
                      <div style={{ color: x.leaveOut ? "var(--warn)" : "var(--bad)" }}>
                        {x.read.problem_count} line{x.read.problem_count === 1 ? "" : "s"} didn't read cleanly:
                        {/* long-list-ok: five problem lines at most, and none of them is a checkbox -- the one
                            checkbox below is for the whole file. */}
                        {x.read.problems.slice(0, 5).map((p) => (
                          <div key={p.line} className="muted" style={{ fontSize: 12 }}>line {p.line}: {p.why} — "{p.text.slice(0, 80)}"</div>
                        ))}
                        {/* LEAVING THEM OUT IS SOMETHING SOMEBODY CHOOSES. A whole day's roster is not
                            held back by one bad line, and the bad lines are never kept quietly. */}
                        {x.read.vuids > 0 && (
                          <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4, color: "var(--text)", cursor: "pointer" }}>
                            <input type="checkbox" checked={!!x.leaveOut} onChange={(e) => leaveOut(x.id, e.target.checked)} />
                            Leave {x.read.problem_count === 1 ? "that line" : "those lines"} out and save the other {x.read.vuids.toLocaleString()}
                          </label>
                        )}
                      </div>
                    )}
                    {x.state === "error" && <span style={{ color: "var(--bad)" }}>{x.err}</span>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {x.state !== "saving" && x.state !== "saved" && x.state !== "reading" && (
                      <button type="button" className="btn sm" title="Remove this file" onClick={() => remove(x.id)}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {checkErr && <div className="err" style={{ marginTop: 10 }}>Couldn't check these against Orator. {checkErr}</div>}
        {daysErr && <div className="err" style={{ marginTop: 10 }}>Couldn't load the Election calendar. {daysErr}</div>}
        {check && !done && (
          <div style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            {/* WHO IT IS FOR, said before Save: a list only touches the campaigns in its election. */}
            <div style={{ color: check.campaigns_on_day.length ? "var(--text)" : "var(--warn)" }}>
              {check.campaigns_on_day.length
                ? <>For the campaigns in the {day(election)} election: {check.campaigns_on_day.map((c) => c.campaign).join(", ")}</>
                : <>No campaign is in the {day(election)} election, so this list won't take anyone off a walkbook, call list or text list.</>}
            </div>
            <b>{totalVoters.toLocaleString()}</b> voters · <b>{check.in_orator.toLocaleString()}</b> are voters in Orator
            {check.already > 0 && <> · {check.already.toLocaleString()} already on an earlier list</>}
            <div className="muted" style={{ fontSize: 13 }}>
              {check.by_campaign.length === 0
                ? "None of them are in a walkbook that is out right now."
                : <>In walkbooks out now: {check.by_campaign.map((c) =>
                    `${c.campaign} — ${c.doors.toLocaleString()} door${c.doors === 1 ? "" : "s"} leave (${c.voters.toLocaleString()} targeted voters)`).join(" · ")}</>}
            </div>
            {/* THE CALL AND TEXT LISTS of the same campaigns (0419). */}
            <div className="muted" style={{ fontSize: 13 }}>
              {(check.phones_by_campaign ?? []).length === 0
                ? "None of them are on a call or text list that is out right now."
                : <>On call and text lists out now: {(check.phones_by_campaign ?? []).map((c) =>
                    `${c.campaign} — ${phoneLine("call", c.calls)}, ${phoneLine("text", c.texts)}`).join(" · ")}</>}
            </div>
          </div>
        )}

        {pending.length > 0 && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
            <button className="btn accent" onClick={save} disabled={!canSave}>
              {saving ? "Saving…" : `Save ${pending.length} file${pending.length === 1 ? "" : "s"}`}
            </button>
            {!method && !reading && <span className="muted" style={{ fontSize: 13 }}>Choose how they voted first.</span>}
            {blocked && !reading && <span style={{ color: "var(--bad)", fontSize: 13 }}>A file above can't be saved as it is — remove it with ✕, or leave its bad lines out.</span>}
          </div>
        )}
        {done && <div style={{ marginTop: 10, fontSize: 14, color: "var(--good)" }}>{done}</div>}
      </div>

      <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <div className="row" style={{ gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800 }}>Lists on file</div>
          {list && <span className="muted" style={{ fontSize: 13 }}>
            Counting now: {list.counting.toLocaleString()} voters, {list.counting_in_orator.toLocaleString()} of them in Orator
          </span>}
        </div>
        {listErr && <div className="err" style={{ marginTop: 8 }}>Couldn't load the lists. {listErr}</div>}
        {!listErr && list && list.uploads.length === 0 && <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>No lists yet.</div>}
        {!listErr && list && list.uploads.length > 0 && (
          <table style={{ width: "100%", marginTop: 10, fontSize: 13 }}>
            <thead><tr className="muted" style={{ textAlign: "left" }}>
              <th>File</th><th>How</th><th>Ballot dates</th><th>Voters</th><th>In Orator</th><th>Election</th><th>For</th><th>Added</th><th></th>
            </tr></thead>
            <tbody>
              {list.uploads.map((u) => (
                <tr key={u.id} style={{ opacity: u.counting ? 1 : 0.5 }}>
                  <td>{u.file_name || "—"}</td>
                  <td>{methodLabel(u.method)}</td>
                  <td>{span(u.first_cast, u.last_cast)}</td>
                  <td>{u.vuid_count.toLocaleString()}</td>
                  <td>{u.in_orator.toLocaleString()}</td>
                  <td>{day(u.election_date)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {u.campaign ? `${u.campaign} only` : u.campaigns_on_day.length ? u.campaigns_on_day.join(", ") : "—"}
                  </td>
                  <td className="muted">{new Date(u.uploaded_at).toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}{u.uploaded_by ? ` · ${u.uploaded_by}` : ""}</td>
                  <td style={{ textAlign: "right" }}>
                    {u.removed_at ? <span className="muted">taken back</span>
                      : !u.counting ? <span className="muted">election over</span>
                      : <button className="btn sm warn" onClick={() => takeBack(u)}>Take back</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
